import { Alert, Box, Grid, Typography } from "@mui/material";
import imgBg from "assets/home/template3.png";
import { LIGHT_GREY } from "styles/oneOffColor";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { trackEvent } from "utils/tracking";

interface Props {
  handleNext: (method?: TemplateMethod) => void;
  isDialog?: boolean;
}

enum TemplateMethod {
  from_scratch = "from_scratch",
  use_bento_ai = "use_bento_ai",
}

const styles = {
  iconContainer: {
    fontSize: { xs: 20, md: 40 },
    mb: 1.5,
  },
  aiCard: () => ({
    p: 4,
    background: `url(${imgBg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderRadius: 4,
    cursor: "default",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
  }),
  nonAiCard: (isDialog?: boolean) => ({
    p: 4,
    backgroundColor: isDialog ? "transparent" : "white",
    border: `1px solid ${LIGHT_GREY}`,
    borderRadius: 4,
    cursor: "default",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
  }),
  dialogAnnotation: {
    backgroundColor: "purple.light",
    borderRadius: 6,
    fontSize: { xs: 12, md: 16 },
    color: "black",
    my: 3,
  },
};

const PickTemplateMethodForm = ({ handleNext, isDialog = true }: Props) => {
  const pickMethod = (method: TemplateMethod) => {
    trackEvent("Create Template - Pick Method", {
      method,
    });
    handleNext(method);
  };

  return (
    <>
      {isDialog && (
        <Box>
          <Typography sx={{ fontSize: 16 }}>
            Chose how you'd like to get started
          </Typography>

          <Alert
            icon={
              <Box
                component={"i"}
                className="fa-regular fa-circle-info"
                sx={{ color: "purple.dark" }}
              />
            }
            severity="info"
            sx={styles.dialogAnnotation}
          >
            <strong>Well crafted templates</strong> improve response rate{" "}
            <Box
              component="span"
              sx={{ color: "purple.dark", fontWeight: 600 }}
            >
              up to 300%
            </Box>
          </Alert>
        </Box>
      )}

      <Grid container>
        <Grid container gap={3}>
          {/* AI Card */}
          <Grid item xs={12} md={6} sx={styles.aiCard()}>
            <Box sx={[styles.iconContainer]}>🚀</Box>
            <Typography variant="h4">Use AI Assistant</Typography>

            <Typography sx={{ mt: 2, flexGrow: 1, fontSize: 16 }}>
              Answer a few quick questions and let our AI craft a personalized,
              high-converting template for you
            </Typography>
            <Box sx={{ mt: 3 }}>
              <StyledLargeButton
                fullWidth
                onClick={() => pickMethod(TemplateMethod.use_bento_ai)}
              >
                Get Started
              </StyledLargeButton>
            </Box>
          </Grid>

          {/* From Scratch Card */}
          <Grid item xs md sx={styles.nonAiCard(isDialog)}>
            <Box sx={[styles.iconContainer]}>✏️</Box>
            <Typography variant="h4">Write From Scratch</Typography>

            <Typography sx={{ mt: 2, flexGrow: 1, fontSize: 16 }}>
              Create your own template manually with control over every word and
              detail
            </Typography>
            <Box textAlign="center" sx={{ mt: 3 }}>
              <StyledLargeButton
                color="secondary"
                fullWidth
                onClick={() => pickMethod(TemplateMethod.from_scratch)}
              >
                Start Writing
              </StyledLargeButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default PickTemplateMethodForm;
